/**
 * i18n Configuration with Lazy Loading
 *
 * Uses Vite dynamic imports for on-demand translation loading.
 * Only loads the selected language instead of all 6 languages upfront.
 *
 * Performance:
 * - Before: ~1.3 MB (all 126 translation files loaded)
 * - After: ~220 KB (21 namespaces for selected language only)
 * - Bundle reduction: ~80%
 *
 * @module i18n
 */

import i18n, { type InitOptions, type i18n as I18n } from 'i18next';
import { initReactI18next } from 'react-i18next';

/**
 * Extended i18n interface with methods that exist at runtime
 * but aren't included in the base TypeScript definitions
 */
interface ExtendedI18n extends I18n {
  isInitialized: boolean;
  addResourceBundle(
    lng: string,
    ns: string,
    resources: Record<string, unknown>,
    deep?: boolean,
    overwrite?: boolean
  ): void;
  hasResourceBundle(lng: string, ns: string): boolean;
}

/**
 * Supported languages
 */
export const SUPPORTED_LANGUAGES = ['en', 'de', 'ar'] as const;
export type AppLanguage = typeof SUPPORTED_LANGUAGES[number];

/**
 * All translation namespaces
 */
export const NAMESPACES = [
  'common',
  'nav',
  'overview',
  'profile',
  'dashboard',
  'auth',
  'employees',
  'settings',
  'leave',
  'timeTracking',
  'admin',
  'reports',
  'layout',
  'quickActions',
  'website',
  'setup',
  'import',
  'fields',
  'fieldGroups',
  'roles',
  'compensation',
  'expenses',
  'compliance',
  'recruiting'
] as const;

export type Namespace = typeof NAMESPACES[number];

/**
 * Fallback language for missing translations
 */
const FALLBACK_LANGUAGE: AppLanguage = 'en';

/**
 * Detects the user's preferred language from localStorage or browser settings
 *
 * Priority:
 * 1. localStorage preference (persisted from previous session)
 * 2. Browser language (navigator.language)
 * 3. Fallback to English
 *
 * @returns Detected language code
 */
function detectLanguage(): AppLanguage {
  // Server-side rendering check
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return FALLBACK_LANGUAGE;
  }

  // Check localStorage for persisted language preference
  const stored = localStorage.getItem('i18nextLng');
  if (stored && SUPPORTED_LANGUAGES.includes(stored as AppLanguage)) {
    return stored as AppLanguage;
  }

  // Detect from browser language
  const browserLang = navigator.language || navigator.languages?.[0] || '';
  const langCode = browserLang.toLowerCase().split('-')[0];

  // Map browser language codes to supported languages
  const languageMap: Record<string, AppLanguage> = {
    en: 'en',
    de: 'de',
    ar: 'ar'
  };

  return languageMap[langCode] || FALLBACK_LANGUAGE;
}

/**
 * Resource type for a single namespace
 */
type NamespaceResource = Record<string, unknown>;

/**
 * Complete language resources for all namespaces
 */
type LanguageResources = Record<Namespace, NamespaceResource>;

/**
 * Loads translation resources for a specific language using Vite dynamic imports
 *
 * Benefits of dynamic imports:
 * - Code splitting: Each language is bundled separately
 * - Lazy loading: Only load what's needed
 * - Type safety: TypeScript validates import paths at build time
 * - Caching: Vite generates content-hashed chunks for optimal caching
 * - Security: All resources bundled at build time (no runtime HTTP requests)
 *
 * @param lng - Language code to load
 * @returns Promise resolving to language resources for all namespaces
 * @throws Error if language is not supported or resources fail to load
 */
async function loadLanguageResources(lng: AppLanguage): Promise<LanguageResources> {
  try {
    // Validate language
    if (!SUPPORTED_LANGUAGES.includes(lng)) {
      console.warn(`[i18n] Unsupported language: ${lng}, falling back to ${FALLBACK_LANGUAGE}`);
      lng = FALLBACK_LANGUAGE;
    }

    // Loading language resources (debug logging)

    // Dynamic imports - Vite will code-split these into separate chunks
    // Each import is transformed to: import('./locales/[lng]/[namespace].json')
    const [
      common,
      nav,
      overview,
      profile,
      dashboard,
      auth,
      employees,
      settings,
      leave,
      timeTracking,
      admin,
      reports,
      layout,
      quickActions,
      website,
      setup,
      importNs, // 'import' is a reserved keyword
      fields,
      fieldGroups,
      roles,
      compensation,
      expenses,
      compliance,
      recruiting
    ] = await Promise.all([
      import(`./locales/${lng}/common.json`),
      import(`./locales/${lng}/nav.json`),
      import(`./locales/${lng}/overview.json`),
      import(`./locales/${lng}/profile.json`),
      import(`./locales/${lng}/dashboard.json`),
      import(`./locales/${lng}/auth.json`),
      import(`./locales/${lng}/employees.json`),
      import(`./locales/${lng}/settings.json`),
      import(`./locales/${lng}/leave.json`),
      import(`./locales/${lng}/timeTracking.json`),
      import(`./locales/${lng}/admin.json`),
      import(`./locales/${lng}/reports.json`),
      import(`./locales/${lng}/layout.json`),
      import(`./locales/${lng}/quickActions.json`),
      import(`./locales/${lng}/website.json`),
      import(`./locales/${lng}/setup.json`),
      import(`./locales/${lng}/import.json`),
      import(`./locales/${lng}/fields.json`),
      import(`./locales/${lng}/fieldGroups.json`),
      import(`./locales/${lng}/roles.json`),
      import(`./locales/${lng}/compensation.json`),
      import(`./locales/${lng}/expenses.json`),
      import(`./locales/${lng}/compliance.json`),
      import(`./locales/${lng}/recruiting.json`)
    ]);

    // Successfully loaded all namespaces

    // Map imports to namespace keys
    // Using .default because JSON imports export default
    return {
      common: common.default,
      nav: nav.default,
      overview: overview.default,
      profile: profile.default,
      dashboard: dashboard.default,
      auth: auth.default,
      employees: employees.default,
      settings: settings.default,
      leave: leave.default,
      timeTracking: timeTracking.default,
      admin: admin.default,
      reports: reports.default,
      layout: layout.default,
      quickActions: quickActions.default,
      website: website.default,
      setup: setup.default,
      import: importNs.default,
      fields: fields.default,
      fieldGroups: fieldGroups.default,
      roles: roles.default,
      compensation: compensation.default,
      expenses: expenses.default,
      compliance: compliance.default,
      recruiting: recruiting.default
    };
  } catch (error) {
    console.error(`[i18n] Failed to load resources for ${lng}:`, error);

    // If loading fails and we're not already trying fallback language
    if (lng !== FALLBACK_LANGUAGE) {
      console.warn(`[i18n] Attempting fallback to ${FALLBACK_LANGUAGE}`);
      return loadLanguageResources(FALLBACK_LANGUAGE);
    }

    // If even fallback fails, throw error
    throw new Error(`Failed to load translation resources for ${lng}: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

/**
 * Updates document attributes for language and direction
 *
 * Sets:
 * - dir: 'rtl' for Arabic, 'ltr' for all other languages
 * - lang: Current language code
 *
 * @param lng - Language code
 */
function updateDocumentAttributes(lng: AppLanguage): void {
  const direction = lng === 'ar' ? 'rtl' : 'ltr';
  document.documentElement.setAttribute('dir', direction);
  document.documentElement.setAttribute('lang', lng);
}

/**
 * Initializes i18n with lazy-loaded resources
 *
 * Process:
 * 1. Detect user's preferred language
 * 2. Initialize i18n with basic config (no resources yet)
 * 3. Load resources for detected language
 * 4. Add loaded resources to i18n
 * 5. Update document attributes
 * 6. Set up event listeners
 *
 * @returns Promise that resolves when i18n is fully initialized
 */
async function initializeI18n(): Promise<void> {
  try {
    const detectedLanguage = detectLanguage();

    // Load resources BEFORE initializing i18n
    // This avoids timing issues with addResourceBundle
    const resources = await loadLanguageResources(detectedLanguage);

    // Build the resources object in i18next format
    const i18nResources: Record<string, Record<string, Record<string, unknown>>> = {
      [detectedLanguage]: {}
    };

    Object.entries(resources).forEach(([namespace, resource]) => {
      i18nResources[detectedLanguage][namespace] = resource as Record<string, unknown>;
    });

    // Initialize i18n with resources already loaded
    const initOptions: InitOptions = {
      lng: detectedLanguage,
      fallbackLng: FALLBACK_LANGUAGE,
      ns: [...NAMESPACES], // Spread to create mutable array
      defaultNS: 'common',
      interpolation: {
        escapeValue: false // React already escapes values
      },
      resources: i18nResources
    };

    await i18n
      .use(initReactI18next)
      .init(initOptions);

    // Update document attributes
    updateDocumentAttributes(detectedLanguage);

    // Set up language change listener
    i18n.on('languageChanged', async (lng: string) => {
      const newLang = lng as AppLanguage;

      // Persist to localStorage
      localStorage.setItem('i18nextLng', newLang);

      // Update document attributes
      updateDocumentAttributes(newLang);

      // Check if resources for this language are already loaded
      // Using type assertion because hasResourceBundle exists at runtime
      if (!(i18n as ExtendedI18n).hasResourceBundle(newLang, 'common')) {
        // Language changed, loading new resources

        try {
          // Load resources for new language
          const newResources = await loadLanguageResources(newLang);

          // Add all namespace resources
          // Using type assertion because addResourceBundle exists at runtime
          Object.entries(newResources).forEach(([namespace, resource]) => {
            (i18n as ExtendedI18n).addResourceBundle(newLang, namespace, resource, true, true);
          });

          // Resources loaded successfully
        } catch (error) {
          console.error(`[i18n] Failed to load resources for ${newLang}:`, error);

          // If loading new language fails, revert to fallback
          if (newLang !== FALLBACK_LANGUAGE) {
            console.warn(`[i18n] Reverting to fallback language: ${FALLBACK_LANGUAGE}`);
            await i18n.changeLanguage(FALLBACK_LANGUAGE);
          }
        }
      }
    });

    // Initialization complete
  } catch (error) {
    console.error('[i18n] Initialization failed:', error);

    // Critical error - show user-friendly error
    throw new Error(`Failed to initialize translations: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Initialize i18n
// This returns a promise, but we export the i18n instance immediately
// Components should check i18n.isInitialized or use Suspense
void initializeI18n();

// Export i18n instance
export default i18n;

/**
 * Helper function to check if i18n is ready
 * Useful for loading states in components
 *
 * @returns true if i18n has loaded resources
 */
export function isI18nReady(): boolean {
  // Using type assertions because these properties exist at runtime
  return (i18n as ExtendedI18n).isInitialized && (i18n as ExtendedI18n).hasResourceBundle(i18n.language, 'common');
}

/**
 * Helper function to change language with resource loading
 * Use this instead of i18n.changeLanguage() for guaranteed resource loading
 *
 * IMPORTANT: Resources are loaded BEFORE changing the language to ensure
 * React components re-render with translations already available.
 * This fixes the race condition where UI shows translation keys instead of text.
 *
 * @param lng - Language to switch to
 * @returns Promise that resolves when language is changed and resources loaded
 */
export async function changeLanguage(lng: AppLanguage): Promise<void> {
  if (!SUPPORTED_LANGUAGES.includes(lng)) {
    console.warn(`[i18n] Unsupported language: ${lng}`);
    return;
  }

  // Skip if already on this language
  if (i18n.language === lng) {
    return;
  }

  // Load resources BEFORE changing language to avoid race condition
  // This ensures translations are available when React components re-render
  const extendedI18n = i18n as ExtendedI18n;
  if (!extendedI18n.hasResourceBundle(lng, 'common')) {
    try {
      const resources = await loadLanguageResources(lng);
      Object.entries(resources).forEach(([namespace, resource]) => {
        extendedI18n.addResourceBundle(lng, namespace, resource, true, true);
      });
    } catch (error) {
      console.error(`[i18n] Failed to load resources for ${lng}:`, error);
      return;
    }
  }

  // NOW change language - React will re-render with translations ready
  await i18n.changeLanguage(lng);
}
